// ─────────────────────────────────────────────────────────────────────────────
//  DairyPro Bluetooth Printer  —  src/utils/bluetoothPrinter.js
//  ESC/POS commands for generic 2-inch and 3-inch thermal printers via Web Bluetooth
// ─────────────────────────────────────────────────────────────────────────────

// Bluetooth GATT service/characteristic UUIDs used by most ESC/POS printers
const PRINTER_SERVICE_UUID       = '000018f0-0000-1000-8000-00805f9b34fb';
const PRINTER_CHAR_UUID          = '00002af1-0000-1000-8000-00805f9b34fb';

// Fallback UUIDs some printers use
const ALT_SERVICE_UUID           = '49535343-fe7d-4ae5-8fa9-9fafd205e455';
const ALT_CHAR_UUID              = '49535343-1e4d-4bd9-ba61-23c647249616';

// ESC/POS command constants
const ESC  = 0x1B;
const GS   = 0x1D;

// ── Low-level ESC/POS byte builders ──────────────────────────────────────────

const CMD = {
  init:          () => [ESC, 0x40],
  feedLines:     (n) => [ESC, 0x64, n],
  cutPaper:      ()  => [GS, 0x56, 0x41, 0x03],
  boldOn:        ()  => [ESC, 0x45, 1],
  boldOff:       ()  => [ESC, 0x45, 0],
  alignLeft:     ()  => [ESC, 0x61, 0],
  alignCenter:   ()  => [ESC, 0x61, 1],
  alignRight:    ()  => [ESC, 0x61, 2],
  fontNormal:    ()  => [ESC, 0x21, 0x00],        // normal size
  fontDouble:    ()  => [ESC, 0x21, 0x30],        // double width + height
  fontWide:      ()  => [ESC, 0x21, 0x20],        // double width only
  underlineOn:   ()  => [ESC, 0x2D, 1],
  underlineOff:  ()  => [ESC, 0x2D, 0],
};

/**
 * Encode a string to Uint8Array (Latin-1 / ASCII safe for 2-inch printers).
 * Characters outside Latin-1 are replaced with '?'.
 */
function encode(str) {
  const normalized = str.replace(/₹/g, 'Rs.');
  return Uint8Array.from(normalized, c => {
    const code = c.charCodeAt(0);
    return code <= 255 ? code : 63;
  });
}

/**
 * Format a two-column line: label on left, value on right, total width 32.
 */
function twoCol(label, value, width = 32) {
  const l = String(label);
  const v = String(value);
  const gap = width - l.length - v.length;
  if (gap <= 0) return l.slice(0, width - v.length - 1) + ' ' + v;
  return l + ' '.repeat(gap) + v;
}

/**
 * Draw a dashed separator line (32 chars for 2-inch / 58mm paper).
 */
function separator(char = '-', width = 32) {
  return char.repeat(width);
}

// ── Slip builder ─────────────────────────────────────────────────────────────

/**
 * Build complete ESC/POS byte array for a collection slip.
 *
 * @param {Object} opts
 * @param {string}  opts.centerName
 * @param {string}  opts.centerCode
 * @param {string}  opts.date          ISO date string
 * @param {number}  opts.shift         1=Morning 2=Evening
 * @param {string}  opts.farmerCode
 * @param {string}  opts.farmerName
 * @param {string}  opts.milkType      'Cow' | 'Buffalo'
 * @param {number}  opts.quantity
 * @param {number}  opts.fat
 * @param {number}  opts.snf
 * @param {number}  opts.clr
 * @param {number}  opts.rate
 * @param {number}  opts.amount
 * @param {boolean} opts.isOffline     true if saved locally
 * @param {string}  opts.paperWidth    '2inch' | '3inch'
 */
export function buildCollectionSlip(opts) {
  const {
    centerName = 'Dairy Center',
    centerCode = '',
    date = '',
    shift = 1,
    farmerCode = '',
    farmerName = '',
    milkType = 'Cow',
    quantity = 0,
    fat = 0,
    snf = 0,
    clr = 0,
    rate = 0,
    amount = 0,
    isOffline = false,
    paperWidth = '2inch',
  } = opts;

  const lineWidth = paperWidth === '3inch' ? 48 : 32;
  const doubleWidthChars = paperWidth === '3inch' ? 22 : 14;

  const shiftLabel  = shift === 1 ? 'Morning' : 'Evening';
  const dateStr     = date ? new Date(date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : date;
  const timeStr     = new Date().toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });

  const bytes = [];

  const push = (...cmds) => { for (const c of cmds) bytes.push(...(Array.isArray(c) ? c : [c])); };
  const text = (str)      => { const arr = encode(str + '\n'); bytes.push(...arr); };
  const raw  = (str)      => { const arr = encode(str);        bytes.push(...arr); };

  // ── Init ──────────────────────────────────────────
  push(CMD.init());
  push(CMD.alignCenter());
  push(CMD.boldOn());
  push(CMD.fontDouble());
  text(centerName.slice(0, doubleWidthChars));

  push(CMD.fontNormal());
  push(CMD.boldOff());
  if (centerCode) text(`Code: ${centerCode}`);
  text(separator('=', lineWidth));

  // ── Slip header ───────────────────────────────────
  push(CMD.alignLeft());
  push(CMD.boldOn());
  text('MILK COLLECTION SLIP');
  push(CMD.boldOff());
  text(separator('-', lineWidth));

  raw(twoCol('Date:', dateStr, lineWidth) + '\n');
  raw(twoCol('Shift:', shiftLabel, lineWidth) + '\n');
  if (isOffline) {
    push(CMD.boldOn());
    text('** OFFLINE SAVED **');
    push(CMD.boldOff());
  }
  text(separator('-', lineWidth));

  // ── Farmer ────────────────────────────────────────
  raw(twoCol('Farmer:', farmerCode, lineWidth) + '\n');
  raw(twoCol('Name:', farmerName.slice(0, lineWidth - 8), lineWidth) + '\n');
  raw(twoCol('Milk:', milkType, lineWidth) + '\n');
  text(separator('-', lineWidth));

  // ── Measurements ─────────────────────────────────
  raw(twoCol('Quantity:', `${parseFloat(quantity).toFixed(3)} L`, lineWidth) + '\n');
  raw(twoCol('FAT:', parseFloat(fat).toFixed(2), lineWidth) + '\n');
  raw(twoCol('SNF:', parseFloat(snf).toFixed(2), lineWidth) + '\n');
  if (parseFloat(clr) > 0)
    raw(twoCol('CLR:', parseFloat(clr).toFixed(2), lineWidth) + '\n');
  raw(twoCol('Rate:', `Rs.${parseFloat(rate).toFixed(2)}`, lineWidth) + '\n');
  text(separator('=', lineWidth));

  // ── Amount ────────────────────────────────────────
  push(CMD.alignCenter());
  push(CMD.boldOn());
  push(CMD.fontDouble());
  text(`Rs.${parseFloat(amount).toFixed(2)}`);
  push(CMD.fontNormal());
  push(CMD.boldOff());
  push(CMD.alignLeft());
  text(separator('=', lineWidth));

  // ── Footer ───────────────────────────────────────
  push(CMD.alignCenter());
  text(`Printed: ${dateStr} ${timeStr}`);
  text('Thank you!');
  push(CMD.feedLines(4));
  push(CMD.cutPaper());

  return new Uint8Array(bytes);
}

// ── Bluetooth connection manager ─────────────────────────────────────────────

let _device    = null;
let _server    = null;
let _charWrite = null;

/**
 * Check if Web Bluetooth is available in this browser.
 */
export function isBluetoothSupported() {
  return typeof navigator !== 'undefined' && 'bluetooth' in navigator;
}

/**
 * Connect to a Bluetooth thermal printer.
 * Tries primary UUIDs first, then fallback.
 * Returns { success: true } or { success: false, error }.
 */
export async function connectPrinter() {
  if (!isBluetoothSupported())
    return { success: false, error: 'Web Bluetooth not supported in this browser. Use Chrome on Android.' };

  try {
    _device = await navigator.bluetooth.requestDevice({
      // Show every nearby BLE device. Many generic thermal printers use an
      // unexpected device name, so name filters can hide a valid printer.
      acceptAllDevices: true,
      optionalServices: [PRINTER_SERVICE_UUID, ALT_SERVICE_UUID],
    });

    _server = await _device.gatt.connect();

    // Try primary service
    let service, char;
    try {
      service = await _server.getPrimaryService(PRINTER_SERVICE_UUID);
      char    = await service.getCharacteristic(PRINTER_CHAR_UUID);
    } catch {
      // Try fallback
      service = await _server.getPrimaryService(ALT_SERVICE_UUID);
      char    = await service.getCharacteristic(ALT_CHAR_UUID);
    }

    _charWrite = char;

    _device.addEventListener('gattserverdisconnected', () => {
      _device = null; _server = null; _charWrite = null;
    });

    return { success: true, name: _device.name || 'Printer' };
  } catch (err) {
    _device = null; _server = null; _charWrite = null;
    if (err.name === 'NotFoundError') return { success: false, error: 'No printer selected.' };
    if (err.name === 'SecurityError')  return { success: false, error: 'Bluetooth permission denied.' };
    return { success: false, error: err.message || 'Connection failed.' };
  }
}

/**
 * Disconnect the current printer.
 */
export function disconnectPrinter() {
  if (_device?.gatt?.connected) _device.gatt.disconnect();
  _device = null; _server = null; _charWrite = null;
}

/**
 * Whether a printer is currently connected.
 */
export function isPrinterConnected() {
  return !!(  _device && _device.gatt?.connected && _charWrite);
}

/**
 * Print raw bytes to the connected printer.
 * Splits into 512-byte chunks (BLE MTU safety).
 */
async function printRaw(data) {
  if (!_charWrite) throw new Error('Printer not connected.');
  const CHUNK = 512;
  for (let i = 0; i < data.length; i += CHUNK) {
    const chunk = data.slice(i, i + CHUNK);
    await _charWrite.writeValue(chunk);
    await new Promise(r => setTimeout(r, 50));  // small delay between chunks
  }
}

/**
 * Print a collection slip. Connects if not already connected.
 * Returns { success: true } or { success: false, error }.
 */
export async function printCollectionSlip(slipData) {
  try {
    if (!isPrinterConnected()) {
      const conn = await connectPrinter();
      if (!conn.success) return conn;
    }
    const bytes = buildCollectionSlip(slipData);
    await printRaw(bytes);
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message || 'Print failed.' };
  }
}

/**
 * Get the name of the connected printer (or null).
 */
export function getPrinterName() {
  return _device?.name || null;
}
